import styled from '@emotion/styled'
import { yupResolver } from '@hookform/resolvers/yup'
import ReactJson from '@microlink/react-json-view'
import type React from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { useCardBalanceConfig, useCreateCardBalanceConfig } from '../../api/react-query'
import { TextFieldController } from '../../components/react-hook-components.tsx'
import TextContainer from '../../components/TextContainer.tsx'
import { TooltipChangeAccountStatus } from '../../components/Tooltips.tsx'
import ButtonIconChangeStatus from '../../figma/images/buttonIconChangeStatus.tsx'
import Spacings from '../../figma/tokens/Spacings.ts'
import { BorderContainer, BorderContainerHeader } from '../../helpers/CreditOnCardHelpers.tsx'
import { captureException } from '../../helpers/sentryHelpers.ts'
import useMaterialNotification from '../../hooks/useMaterialNotification.tsx'
import FigmaBox from '../../mynt-components/components/FigmaBox.tsx'
import FigmaButton from '../../mynt-components/components/FigmaButton.tsx'
import MaterialSwitch from '../../mynt-components/components/MaterialSwitch.tsx'
import { GenericObjectViewContainer, Line, UnstyledButton } from '../../mynt-components/components/StyledComponents.tsx'
import {
  BO_CUSTOMER_CARD_FORM_MIN_WIDTH,
  BO_TWO_COLUMN_SIZE_LEFT,
  BO_TWO_COLUMN_SIZE_RIGHT,
} from '../../mynt-components/WeakHardCodedSizes.ts'
import { getText, TextKeys } from '../../tiger/libs/TextRepository.ts'

type Props = {
  customerId: string
  setIsChangeStatusModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

type FormValues = { threshold?: number }

const errorMessage = getText(TextKeys.mFautoTopUpFallsBelowError, 'figma')
const ValidationSchema = Yup.object().shape({
  threshold: Yup.number().required(errorMessage).typeError(errorMessage),
})

export default function CustomerCardTabLowBalanceAlert({ customerId, setIsChangeStatusModalOpen }: Props) {
  const notify = useMaterialNotification()

  const [isMutating, setIsMutating] = useState<boolean>(false)

  const { data: cardBalanceConfig, isLoading } = useCardBalanceConfig(customerId)

  const { control, handleSubmit } = useForm<FormValues>({
    values: { threshold: cardBalanceConfig?.notification?.threshold },
    resolver: yupResolver(ValidationSchema),
  })

  const statusSetByCustomer = cardBalanceConfig?.notification?.active
  const mutation = useCreateCardBalanceConfig(customerId)

  const onToggleNotifications = async () => {
    try {
      setIsMutating(true)

      await mutation.mutateAsync({
        ...cardBalanceConfig,
        notification: {
          threshold: cardBalanceConfig?.topUp?.threshold,
          active: !cardBalanceConfig?.notification?.active,
        },
      })
    } catch (error) {
      if (error instanceof Error) {
        captureException(error)
      } else {
        throw error
      }
    } finally {
      setIsMutating(false)
    }
  }

  const onSubmit = async ({ threshold }: FormValues) => {
    try {
      await mutation.mutateAsync({
        notification: {
          ...cardBalanceConfig?.notification,
          threshold: threshold && Math.trunc(threshold),
        },
      })

      notify('Successfully set low balance limit', 'success')
    } catch (error) {
      if (error instanceof Error) {
        captureException(error)
      } else {
        throw error
      }
    }
  }

  if (isLoading) {
    return null
  }

  return (
    <BorderContainer>
      <BorderContainerHeader>
        <TextContainer textKey={TextKeys.lowBalanceAlertHeading} />

        <TooltipChangeAccountStatus
          content={
            <UnstyledButton onClick={() => setIsChangeStatusModalOpen && setIsChangeStatusModalOpen(true)}>
              <ButtonIconChangeStatus />
            </UnstyledButton>
          }
        />
      </BorderContainerHeader>

      <FigmaBox fullWidth direction="row" justify="space-between" align="stretch" gap={Spacings.medium}>
        <FigmaBox fullWidth direction="column">
          <FigmaBox top={Spacings.small} bottom={Spacings.medium}>
            <TextContainer textKey={TextKeys.customersCardLowBalanceAlertSetLowBalanceAlertLimitHeading} />
          </FigmaBox>
          <FormContainer fullWidth direction="column">
            <TextFieldController control={control} name="threshold" type="number" labelTextKey={TextKeys.mFlowBalanceAlertSetLabel} />
            <FigmaBox top fullWidth>
              <FigmaButton onClick={handleSubmit(onSubmit)} textKey={TextKeys.lowBalanceAlertSetButton} />
            </FigmaBox>
          </FormContainer>
        </FigmaBox>

        <Line vertical />

        <GenericObjectViewContainer fullWidth>
          <TextContainer textKey={TextKeys.customersCardLowBalanceAlertSetLowBalanceAlertDetailsHeading} />
          <FigmaBox fullWidth direction="row" justify="space-between" align="flex-start" gap={Spacings.minimum} top={Spacings.medium}>
            <FigmaBox fullWidth flex={BO_TWO_COLUMN_SIZE_LEFT}>
              <TextContainer textKey={TextKeys.lowBalanceAlertText} />
            </FigmaBox>
            <FigmaBox fullWidth flex={BO_TWO_COLUMN_SIZE_RIGHT} align="flex-end">
              <MaterialSwitch checked={!!statusSetByCustomer} onChange={onToggleNotifications} disabled={isMutating} />
            </FigmaBox>
          </FigmaBox>
        </GenericObjectViewContainer>
      </FigmaBox>

      <FigmaBox top spacing={Spacings.huge}>
        <ReactJson src={cardBalanceConfig || {}} name="" />
      </FigmaBox>
    </BorderContainer>
  )
}

const FormContainer = styled(FigmaBox)`
  min-width: ${BO_CUSTOMER_CARD_FORM_MIN_WIDTH};
`
